/* eslint-disable react/prop-types */
import { Common, color } from '@stockbit/ui';
import { Row, Col, Tooltip } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { parseCountAbbrv2 } from 'utils/general';
import VisibilitySensor from 'react-visibility-sensor';
import { effects, priceAlertSelector } from './slice';
import { Td, Th, EmptyPriceAlert, PriceAlertTd, TableLog } from './styled';
import { ButtonTransparent } from '../styled';

const { Button, Iconmoon, Text } = Common;

const mth = {
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
  eq: '=',
};

const map = {
  active: [2],
  pending: [0, 1, 3],
};

interface PriceAlertProps {
  handleAddPriceAlert: (p1?: any, p2?: any, p3?: any) => void;
  handleShowModalCancel: (p1?: any, p2?: any, p3?: any) => void;
  maxHeight?: number;
}

function PriceAlertContent({
  handleAddPriceAlert = () => {},
  handleShowModalCancel = () => {},
  maxHeight = 100,
}: PriceAlertProps) {
  const dispatch = useDispatch();
  const state = useSelector(priceAlertSelector);
  const visibilityRef = useRef<HTMLDivElement>(null);

  const { data, page, reachEnd } = state;

  const handleLoadMore = useCallback(
    async (isVisible: boolean) => {
      if (!isVisible || reachEnd) return;

      const options = {
        page: page + 1,
        limit: 30,
      };
      dispatch(effects.getPriceAlert(options));
    },
    [page, reachEnd],
  );

  useEffect(() => {
    // @ts-ignore
    dispatch(effects.getPriceAlert());
  }, [dispatch]);

  const getCommandValue = (operator, value) => {
    const cmd = mth[operator] || operator;
    const parsedValue = parseCountAbbrv2(value, 0);
    // @ts-ignore
    const val = parsedValue.length > 7 ? parsedValue.slice(0, 7) : parsedValue;

    return (
      <Tooltip title={parsedValue}>
        {cmd} {val}
      </Tooltip>
    );
  };

  // price alert data is empty
  if (data.length === 0) {
    return (
      <EmptyPriceAlert maxHeight={`${maxHeight}px`}>
        <span>No alert has been set</span>
        <Button className="add-alert" onClick={handleAddPriceAlert}>
          <Row justify="center">
            <Col>
              <Iconmoon className="icon-toolbar_plus" />
            </Col>
            <Col>Create a new alert</Col>
          </Row>
        </Button>
      </EmptyPriceAlert>
    );
  }

  const dataSource = data.map((dataPriceAlert) => {
    const newData = { ...dataPriceAlert };
    const symbolShow = newData.command.symbol_2
      ? newData.command.symbol_2.toUpperCase()
      : newData.command.stock.toUpperCase();

    newData.symbol = <Td>{symbolShow}</Td>;
    newData.key = dataPriceAlert.alert_id;

    return newData;
  });

  const columns = [
    {
      title: <Th>Symbol</Th>,
      key: 'symbol',
      sorter: (a, b) => a.command.stock.localeCompare(b.command.stock),
      render: ({ status, symbol }, record, i: number) => {
        let className = '';
        Object.keys(map).map((index) => {
          if (map[index].indexOf(parseInt(status, 10)) > -1) {
            className = index;
          }
          return index;
        });
        const isLast = i + 1 === dataSource.length;

        return (
          <PriceAlertTd onClick={(e) => handleAddPriceAlert(e, record)}>
            <Row>
              <Col>
                <span className={className} style={{ wordBreak: 'keep-all' }}>
                  {className}
                </span>
              </Col>
              <Col style={{ wordBreak: 'keep-all' }}>{symbol}</Col>
              {isLast && (
                <VisibilitySensor
                  containment={visibilityRef.current}
                  onChange={handleLoadMore}
                >
                  <div>&nbsp;</div>
                </VisibilitySensor>
              )}
            </Row>
          </PriceAlertTd>
        );
      },
    },
    {
      title: <Th align="right">Condition</Th>,
      dataIndex: 'command',
      key: 'command',
      render: ({ operator, value }, record) => (
        <Td
          align="right"
          style={{ wordBreak: 'keep-all' }}
          onClick={(e) => handleAddPriceAlert(e, record)}
        >
          {getCommandValue(operator, value)}
        </Td>
      ),
    },
    {
      title: '',
      key: 'action1',
      width: '40px',
      render: (record) => (
        <Td align="right">
          <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={() => (
                <Text
                  color={color.light}
                  className={`price-alert-note-text-${record?.command?.stock}`}
                >
                  {record.name}
                </Text>
              )}
              className="price-alert-note"
            >
              <Iconmoon className="icon-toolbar_note" size="12px" />
            </Tooltip>
            <ButtonTransparent
              onClick={
                (event) => handleShowModalCancel(event, record.alert_id)
                // eslint-disable-next-line react/jsx-curly-newline
              }
              style={{ marginLeft: '5px' }}
            >
              <Iconmoon className="icon-toolbar_delete" size="12px" />
            </ButtonTransparent>
          </span>
        </Td>
      ),
    },
  ];

  return (
    <div
      style={{ height: 'calc(100% - 41px)', paddingBottom: '10px' }}
      ref={visibilityRef}
    >
      <TableLog
        alignSorter="start"
        style={{ height: '100%', overflowY: 'auto' }}
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        sortDirections={['ascend', 'descend', 'ascend']}
      />
    </div>
  );
}

export default PriceAlertContent;
